// src/components/PostPollBlock.js
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import './PostPollBlock.css';

function formatVoteCount(count = 0) {
  return `${count} vote${count === 1 ? '' : 's'}`;
}

function getTimeLeft(expiresAt, isExpired) {
  if (isExpired || !expiresAt) return 'Final results';

  const now = Date.now();
  const end = new Date(expiresAt).getTime();
  const diff = end - now;

  if (diff <= 0) return 'Final results';

  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (days >= 1) return `${days}d left`;
  if (hours >= 1) return `${hours}h left`;
  if (mins >= 1) return `${mins}m left`;
  return 'Ending soon';
}

export default function PostPollBlock({
  postId,
  poll,
  currentUser,
  onPollVoteUpdate,
}) {
  const [localPoll, setLocalPoll] = useState(poll || null);
  const [submitting, setSubmitting] = useState(false);

  // Keep local UI synced when parent sends fresh poll data
  useEffect(() => {
    setLocalPoll(poll || null);
  }, [poll]);

  const safePoll = localPoll || poll;

  const options = useMemo(() => {
    return Array.isArray(safePoll?.options) ? safePoll.options : [];
  }, [safePoll]);

  const totalVotes = Number(safePoll?.totalVotes || 0);
  const isExpired = Boolean(safePoll?.isExpired);
  const hasVoted = Boolean(safePoll?.hasVoted);

  const shouldShowResults = hasVoted || isExpired;

  const handleVote = async (optionIndex) => {
    if (!postId || !currentUser || submitting) return;
    if (hasVoted || isExpired) return;

    try {
      setSubmitting(true);

      const res = await api.post(`/posts/${postId}/vote`, {
        username: currentUser,
        optionIndex,
      });

      const updatedPoll = res?.data?.poll || res?.data?.post?.poll || null;

      if (updatedPoll) {
        // 1) Instant local update for this mounted component
        setLocalPoll(updatedPoll);

        // 2) Sync parent/Home feed state so it persists across navigation
        if (typeof onPollVoteUpdate === 'function') {
          onPollVoteUpdate(postId, updatedPoll);
        }
      }
    } catch (err) {
      console.error('❌ Poll vote failed:', err?.response?.data || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!safePoll || !options.length) return null;

  return (
    <div className="postpollblock">
      <div className="postpollblock-options">
        {options.map((option, index) => {
          const percent = Number(option?.percentage || 0);
          const isSelected = Boolean(option?.selected);

          return (
            <button
              key={index}
              type="button"
              className={`postpollblock-option ${
                shouldShowResults ? 'show-results' : 'before-vote'
              } ${isSelected ? 'is-selected' : ''}`}
              onClick={() => handleVote(index)}
              disabled={shouldShowResults || submitting}
            >
              {shouldShowResults && (
                <div
                  className={`postpollblock-bar ${isSelected ? 'is-selected' : ''}`}
                  style={{ width: `${percent}%` }}
                />
              )}

              <div className="postpollblock-option-inner">
                <span className="postpollblock-option-text">
                  {option?.text || `Option ${index + 1}`}
                </span>

                {shouldShowResults && (
                  <span className="postpollblock-option-percent">
                    {percent}%
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="postpollblock-footer">
        <span className="postpollblock-meta">
          {formatVoteCount(totalVotes)}
        </span>
        <span className="postpollblock-dot">•</span>
        <span className="postpollblock-meta">
          {getTimeLeft(safePoll?.expiresAt, isExpired)}
        </span>
      </div>
    </div>
  );
}
